import type { Editor } from "@tiptap/core";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type FileRecordExportContextValue = {
  setMarkdownSource: (value: string | null) => void;
  setHtmlSource: (value: string | null) => void;
  setTextSource: (value: string | null) => void;
  setTiptapEditor: (editor: Editor | null) => void;
  getMarkdownSource: () => string | null;
  getHtmlSource: () => string | null;
  getTextSource: () => string | null;
  getTiptapEditor: () => Editor | null;
};

const FileRecordExportContext =
  createContext<FileRecordExportContextValue | null>(null);

export function FileRecordExportProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [markdownSource, setMarkdownSourceState] = useState<string | null>(
    null,
  );
  const [htmlSource, setHtmlSourceState] = useState<string | null>(null);
  const [textSource, setTextSourceState] = useState<string | null>(null);
  const [tiptapEditor, setTiptapEditorState] = useState<Editor | null>(null);

  const setMarkdownSource = useCallback((value: string | null) => {
    setMarkdownSourceState(value);
  }, []);

  const setHtmlSource = useCallback((value: string | null) => {
    setHtmlSourceState(value);
  }, []);

  const setTextSource = useCallback((value: string | null) => {
    setTextSourceState(value);
  }, []);

  const setTiptapEditor = useCallback((editor: Editor | null) => {
    setTiptapEditorState(editor);
  }, []);

  const getMarkdownSource = useCallback(() => markdownSource, [markdownSource]);

  const getHtmlSource = useCallback(() => htmlSource, [htmlSource]);

  const getTextSource = useCallback(() => textSource, [textSource]);

  const getTiptapEditor = useCallback(() => tiptapEditor, [tiptapEditor]);

  const value = useMemo(
    () => ({
      setMarkdownSource,
      setHtmlSource,
      setTextSource,
      setTiptapEditor,
      getMarkdownSource,
      getHtmlSource,
      getTextSource,
      getTiptapEditor,
    }),
    [
      markdownSource,
      htmlSource,
      textSource,
      tiptapEditor,
      setMarkdownSource,
      setHtmlSource,
      setTextSource,
      setTiptapEditor,
      getMarkdownSource,
      getHtmlSource,
      getTextSource,
      getTiptapEditor,
    ],
  );

  return (
    <FileRecordExportContext.Provider value={value}>
      {children}
    </FileRecordExportContext.Provider>
  );
}

export function useFileRecordExport(): FileRecordExportContextValue {
  const ctx = useContext(FileRecordExportContext);
  if (!ctx) {
    throw new Error(
      "useFileRecordExport must be used within FileRecordExportProvider",
    );
  }
  return ctx;
}
